/**
 * SharedReviewPage — THE OUTSIDE READER'S VIEW OF ONE ARTEFACT (`@P280` T0301 · threat T-3).
 *
 * ⚠️ **THIS PAGE IS THE FIRST ANONYMOUS SURFACE THIS PROJECT ADDS**, and everything unusual about it
 * follows from that. An investor opens a link and reads a founder's tagline, deck text or script —
 * without an account, without a session, and without the app's chrome around it. Threat T-3 measured
 * why that is a real decision rather than a convenience: *"an unauthenticated read of the most
 * confidential text the company owns — a deck carries the round, the numbers, sometimes the cap
 * table — so a leaked link is a permanent disclosure, not a session."*
 *
 * SO THE PAGE RENDERS A SHARE, NOT A COMPANY. It shows the artefact and the verdict on it, and it
 * deliberately shows nothing else: no entity name, no round, no founder identity. The server's payload
 * is an allow-list (`pitch_review_link_service.public_payload`), and this page's job is to render what
 * it is given rather than to ask for more — a client that requested extra fields would be the second
 * half of the same leak.
 *
 * THE VERDICT IS THE POINT OF THE SHARE (`@P280`'s primary goal — *"every failure arrives with the
 * fix"*). A reviewer who is shown a score learns nothing; a reviewer shown *"this rule failed, and here
 * is how to fix it"* can answer. So each failing rule renders WITH its fix, exactly as the founder's own
 * review queue renders it.
 *
 * THREE STATES, AND TWO OF THEM ARE REFUSALS. A link that is revoked, expired or forged answers 403; a
 * link whose artefact is gone answers 404. Both are shown as one sentence — *"this link is not open"* —
 * rather than as diagnostics, because the reader cannot act on the difference and the server refuses to
 * tell a stranger more than that (T-3's control includes not becoming an oracle).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Card, Skeleton, Tag, Typography } from "antd";
import { ApiClientError } from "@/services/api/apiClient";
import { fetchSharedReview, type SharedReview } from "@/services/api/pitchApi";

const { Title, Paragraph, Text } = Typography;

/** The kind labels the founder's own queue uses, so one artefact reads the same on both surfaces. */
const KIND_LABELS: Record<string, string> = {
  tagline: "the tagline",
  pitch_1min: "the 1-minute pitch",
  deck: "the deck",
  speaker_script: "the speaker script",
};

interface ReportedRule {
  /** The rule's NUMBER in the kernel — the reader cites it back ("rule 23"). */
  n?: number;
  verdict?: string;
  note?: string;
  fix?: string;
}

/**
 * The rules that FAILED, from the report's own `rules` array.
 *
 * ⚠️ **THIS FUNCTION READ A SHAPE THAT DOES NOT EXIST, AND THE PAGE CONTRADICTED ITSELF BECAUSE OF IT.**
 * Measured in a browser, 2026-10-12 (`be_sha ad0aa242`, a live link): the counts said **2 FAIL** and the
 * page below them said **"No rule failed on this text"** — a review that told a reviewer with two
 * outstanding problems that there was nothing to correct. The first version looked for
 * `report.failed` and `report.results`; the BE's report carries neither. It carries **`rules`**: ninety
 * entries of `{n, verdict, note, fix}`, and the failing ones are `n = 23` and `n = 55` on the tagline
 * this was measured with.
 *
 * SO THE SHAPE IS READ FROM THE REPORT RATHER THAN ASSUMED, and the guard below (the counts vs the
 * list) is what makes a future mismatch VISIBLE instead of silent: a page that cannot show the failures
 * it claims must say so, never claim there are none.
 */
function failedRules(report: SharedReview["report"]): ReportedRule[] {
  const rules = (report as { rules?: unknown })?.rules;
  if (!Array.isArray(rules)) return [];
  return (rules as ReportedRule[]).filter(
    (r) => String(r?.verdict ?? "").toUpperCase() === "FAIL",
  );
}

/** The FAIL count the report DECLARES — the number the page must agree with. */
function declaredFailCount(report: SharedReview["report"]): number {
  const raw = (report as { counts?: Record<string, unknown> })?.counts?.FAIL;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** The BE's own one-sentence verdict and its digest of what to change — its words, not ours. */
function advisory(report: SharedReview["report"]): { headline: string; top_fixes: string[] } {
  const r = report as { headline?: unknown; top_fixes?: unknown } | undefined;
  return {
    headline: typeof r?.headline === "string" ? r.headline : "",
    top_fixes: Array.isArray(r?.top_fixes) ? (r?.top_fixes as unknown[]).map(String) : [],
  };
}

export default function SharedReviewPage({ token }: { token: string }) {
  const [review, setReview] = useState<SharedReview | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const data = await fetchSharedReview(token);
        if (live) setReview(data);
      } catch (err) {
        if (!live) return;
        // The server's own sentence when it gave one; otherwise the honest plain answer. A stranger is
        // told the door is shut, never why — see the header.
        const body = err instanceof ApiClientError ? (err.body as { message?: string } | undefined) : undefined;
        setRefusal(body?.message || "This review link is not open. It may have been closed, or it may have expired.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="shared-review-loading" style={{ maxWidth: 760, margin: "0 auto", padding: "48px 16px" }}>
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </div>
    );
  }

  if (refusal) {
    return (
      <div
        className="shared-review-refused"
        style={{ maxWidth: 760, margin: "0 auto", padding: "48px 16px" }}
      >
        <Alert type="info" showIcon message="This review link is not open" description={refusal} />
      </div>
    );
  }

  const kinds = review?.artefact_kind ? KIND_LABELS[String(review.artefact_kind)] ?? "the artefact" : "the artefact";
  const failures = failedRules(review?.report);
  const counts = review?.report?.counts ?? {};
  const readiness = review?.report?.readiness;
  const { headline, top_fixes } = advisory(review?.report);
  const declaredFails = declaredFailCount(review?.report);
  /** The page's two halves AGREE, or the page says so — never "nothing failed" beside "2 FAIL". */
  const undoneFailures = declaredFails > 0 && failures.length === 0;

  return (
    <div className="shared-review-page" style={{ maxWidth: 760, margin: "0 auto", padding: "48px 16px" }}>
      {/* WHAT THIS IS, said once, at the top — a reader sent a link deserves to know its shape. */}
      <Title level={3} style={{ marginBottom: 4 }}>
        A shared review
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        You were sent <strong>{kinds}</strong> to review. This page shows that one artefact and the
        verdict on it — nothing else, and nobody else's data.
      </Paragraph>

      <Card title={review?.label || "The artefact"} style={{ marginBottom: 16 }}>
        <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
          {review?.artefact_text || <Text type="secondary">(the artefact text is not on this share)</Text>}
        </Paragraph>
      </Card>

      <Card
        title="The verdict"
        extra={
          readiness ? (
            <Tag color={readiness === "ready" ? "green" : "gold"}>{String(readiness).replace(/_/g, " ")}</Tag>
          ) : null
        }
      >
        {headline ? (
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>{headline}</Text>
          </Paragraph>
        ) : null}
        <Paragraph type="secondary">
          {Object.keys(counts).length
            ? Object.entries(counts)
                .map(([k, v]) => `${v} ${k}`)
                .join(" · ")
            : "The report carries no counts."}
        </Paragraph>
        {top_fixes.length ? (
          <div style={{ marginBottom: 16 }}>
            <Text strong>What to change</Text>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
              {top_fixes.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {undoneFailures ? (
          // THE CONTRADICTION GUARD. If the counts declare failures this page cannot list, saying
          // "nothing failed" is a lie told with confidence — the exact defect measured here. The
          // honest answer names the disagreement, so a reader is never given a green light by a
          // report they cannot see.
          <Alert
            type="error"
            showIcon
            message={`This report declares ${declaredFails} failed rule(s) it does not list.`}
            description="The verdict below is what the checker recorded, but the failing rules themselves are missing from this share — treat the counts as the flag and ask the sender for the detail."
          />
        ) : failures.length === 0 ? (
          <Alert type="success" showIcon message="No rule failed on this text — the report has nothing to correct." />
        ) : (
          failures.map((f, i) => (
            <Alert
              key={`${f.n ?? i}`}
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message={f.n ? `Rule ${f.n}` : "A rule failed"}
              description={
                <>
                  {f.note ? <Paragraph style={{ marginBottom: 4 }}>{f.note}</Paragraph> : null}
                  {/* THE FIX IS THE REASON THE REVIEW WAS SENT — never dropped silently when absent. */}
                  {f.fix ? (
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>How to fix it: </Text>
                      {f.fix}
                    </Paragraph>
                  ) : (
                    <Text type="secondary">No fix was recorded for this rule — treat the note as the flag.</Text>
                  )}
                </>
              }
            />
          ))
        )}
      </Card>

      <Paragraph type="secondary" style={{ marginTop: 24, fontSize: 12 }}>
        This link can be closed by whoever sent it, and it lapses on its own. It opens this one artefact
        only.
      </Paragraph>
    </div>
  );
}

/**
 * The route wrapper, because the token arrives in the PATH (`/review/:token`).
 *
 * A path rather than a query string on purpose: the link is what a founder pastes into an email, and a
 * path survives the messengers that mangle a query string or strip a fragment. A missing token cannot
 * happen through this route (the path segment is required to match at all) — so the guard below is for
 * the case React Router hands back an empty string, and it renders the same refusal the server would.
 */
export const SharedReviewRoute = () => {
  const { token } = useParams<{ token: string }>();
  if (!token) {
    return (
      <div className="shared-review-refused" style={{ maxWidth: 760, margin: "0 auto", padding: "48px 16px" }}>
        <Alert type="info" showIcon message="This review link is not open" description="The link is missing its token." />
      </div>
    );
  }
  return <SharedReviewPage token={token} />;
};
